// Funnel routing matches survey options by order_no, never by description. The wording of both
// questions lives in the database and is owned by the product team (SVSP-226); the routing is not.
// Matching on the text made every copy change a cross-repo change that failed silently: an
// unmatched switch fell through to the default and sent the client to the wrong step, which means
// the wrong package.
//
// order_no is stable only as long as nobody reorders the options. Reordering them is a change to
// this file as well.
export const CURRENT_SITUATION = {
  EXISTING_CLIENT: 1,
  ANOTHER_ACCOUNTANT: 2,
  SELF_MANAGED: 3,
} as const;

export const INCOME_TYPE = {
  W2: 1,
  SELF_EMPLOYED: 2,
  MIX: 3,
  PASSIVE: 4,
  RETIRED: 5,
  NEW_BUSINESS: 6,
} as const;

export type IncomeTrack = "business" | "individual";

export type SurveyOption = { attributes?: { order_no?: number } } | null | undefined;

const BUSINESS_INCOME_TYPES: number[] = [
  INCOME_TYPE.SELF_EMPLOYED,
  INCOME_TYPE.MIX,
  INCOME_TYPE.NEW_BUSINESS,
];

const INDIVIDUAL_INCOME_TYPES: number[] = [
  INCOME_TYPE.W2,
  INCOME_TYPE.PASSIVE,
  INCOME_TYPE.RETIRED,
];

// Step 3 of the survey, by the answers to questions 1 and 2.
const STEP_3_BY_SITUATION: Record<number, Record<IncomeTrack, number>> = {
  [CURRENT_SITUATION.EXISTING_CLIENT]: { business: 1.6, individual: 1.3 },
  [CURRENT_SITUATION.ANOTHER_ACCOUNTANT]: { business: 1.4, individual: 1.1 },
  [CURRENT_SITUATION.SELF_MANAGED]: { business: 1.5, individual: 1.2 },
};

export const FIRM_NAME_PLACEHOLDER = "[Firm Name]";

type FirmDetail = {
  firm_name?: string | null;
  theme?: { firm_name?: string | null } | null;
} | null | undefined;

// Branding is where the firm edits its name; the account name is the one given at sign up.
export const getFirmName = (cpaDetail: FirmDetail): string | undefined =>
  cpaDetail?.theme?.firm_name?.trim() || cpaDetail?.firm_name?.trim() || undefined;

export const getOptionOrderNo = (option: SurveyOption): number | undefined =>
  option?.attributes?.order_no;

export const getIncomeTrack = (option: SurveyOption): IncomeTrack | undefined => {
  const orderNo = getOptionOrderNo(option);
  if (orderNo === undefined) {
    return undefined;
  }
  if (BUSINESS_INCOME_TYPES.includes(orderNo)) {
    return "business";
  }
  if (INDIVIDUAL_INCOME_TYPES.includes(orderNo)) {
    return "individual";
  }
  return undefined;
};

// Returns 0 when either answer is unknown, which is what the previous switch did by falling
// through without assigning a step.
export const getStep3 = (
  situationOption: SurveyOption,
  incomeOption: SurveyOption
): number => {
  const situation = getOptionOrderNo(situationOption);
  const track = getIncomeTrack(incomeOption);
  if (situation === undefined || track === undefined) {
    return 0;
  }
  return STEP_3_BY_SITUATION[situation]?.[track] ?? 0;
};

export const isExistingClient = (situationOption: SurveyOption): boolean =>
  getOptionOrderNo(situationOption) === CURRENT_SITUATION.EXISTING_CLIENT;
